import { fetchCampaign, fetchMasterUpdateCheck, fetchPopups } from '@/components/Functional/lib/api/campaign'
import { postCouponRedeem } from '@/components/Functional/lib/api/coupons'
import type { CouponRedeemParams } from '@/components/Functional/lib/api/coupons'
import {
  createCheckoutSession,
  fetchCheckoutSessionDetail,
  fetchProducts,
  fetchTicketOrders,
} from '@/components/Functional/lib/api/ec'
import { fetchGeofenceAreas, recordGeofenceEvent } from '@/components/Functional/lib/api/geofence'
import { fetchMenus } from '@/components/Functional/lib/api/menus'
import {
  fetchMynaviAreas,
  fetchMynaviCollegeDetail,
  fetchMynaviCollegeTypes,
  fetchMynaviCollegesFilter,
  fetchMynaviCollegesFilterCount,
  fetchMynaviCollegesIncremental,
  fetchMynaviCollegesSearch,
  fetchMynaviEmailCheck,
  fetchMynaviJobCategories,
  fetchMynaviOpenCampusCount,
  fetchMynaviOpenCampuses,
  fetchMynaviPointTransactions,
  fetchMynaviStudyFields,
  postMynaviEmail,
  postMynaviEmailResend,
  postMynaviEmailVerify,
  postMynaviWishToVisit,
} from '@/components/Functional/lib/api/mynavi'
import type {
  FetchMynaviCollegesFilterParams,
  FetchMynaviCollegesSearchParams,
  FetchMynaviPointTransactionsParams,
} from '@/components/Functional/lib/api/mynavi'
import {
  fetchNotification,
  fetchNotifications,
  fetchNotificationsUnread,
} from '@/components/Functional/lib/api/notifications'
import { fetchOcrScanRequest, postOcrScanRequest } from '@/components/Functional/lib/api/ocr'
import { acquireQrReward, fetchQrReward } from '@/components/Functional/lib/api/qr-reward'
import {
  fetchQuestionnaire,
  fetchQuestionnaires,
  fetchQuestionnairesType,
  postQuestionnaireAnswer,
} from '@/components/Functional/lib/api/questionnaires'
import {
  fetchReward,
  fetchRewards,
  mergeRewardDetail,
  mergeRewardsWithUserData,
  postReward,
  postRewardTransactionPhotoFrames,
} from '@/components/Functional/lib/api/rewards'
import { fetchSpotShareSns, recordShareSnsTap } from '@/components/Functional/lib/api/share-sns'
import { fetchSheets } from '@/components/Functional/lib/api/sheets'
import {
  fetchSpot,
  fetchSpotCoupons,
  fetchSpotWithLocation,
  fetchSpots,
  postSpotAudioGuides,
  postSpotCouponRedeem,
  postSpotPhotoFrames,
} from '@/components/Functional/lib/api/spots'
import {
  fetchStamp,
  fetchStampQuizzes,
  postOcrStamp,
  postStamp,
  postStampQuizAnswer,
  postStampStrict,
} from '@/components/Functional/lib/api/stamps'
import { postTracking } from '@/components/Functional/lib/api/tracking'
import {
  fetchUserActivities,
  fetchUserProfile,
  fetchUserReward,
  fetchUserRewardTransactions,
  fetchUserRewards,
  fetchUserSheets,
  fetchUserStamp,
  fetchUserStamps,
  postRewardApplicationForm,
  postUserRecovery,
  postUserReset,
  postUserRewardTransaction,
} from '@/components/Functional/lib/api/user'
import type { CreateCheckoutSessionRequest, ListProductsParams } from '@/schemas/v2/ec'
import type { ApplicationFormSubmitRequest } from '@/schemas/v2/reward'

type ApiContext = {
  headers: Record<string, string>
  headersWithAuthorization: Record<string, string>
}

/**
 * ヘッダーをバインドした API 関数群を返すファクトリ。
 *
 * - `guest`: 認証不要 API（headers のみ）
 * - `auth`:  認証必要 API（headersWithAuthorization）
 * - `pure`:  ヘッダー不要な純粋関数
 */
export function createApi({ headers, headersWithAuthorization }: ApiContext) {
  const guestOpts = { headers }
  const authOpts = { headers: headersWithAuthorization }

  return {
    guest: {
      fetchCampaign: () => fetchCampaign(guestOpts),
      fetchPopups: () => fetchPopups(guestOpts),
      fetchSpots: () => fetchSpots(guestOpts),
      fetchSpot: (id: number) => fetchSpot(id, guestOpts),
      fetchSpotWithLocation: (id: number, latitude: number, longitude: number) =>
        fetchSpotWithLocation(id, latitude, longitude, guestOpts),
      fetchSpotCoupons: (id: number) => fetchSpotCoupons(id, guestOpts),
      postSpotPhotoFrames: (id: number, body?: Record<string, unknown>) => postSpotPhotoFrames(id, body, guestOpts),
      postSpotAudioGuides: (id: number, body?: Record<string, unknown>) => postSpotAudioGuides(id, body, guestOpts),
      fetchMasterUpdateCheck: () => fetchMasterUpdateCheck(guestOpts),
      fetchRewards: () => fetchRewards(guestOpts),
      fetchReward: (id: number) => fetchReward(id, guestOpts),
      fetchSheets: () => fetchSheets(guestOpts),
      fetchNotifications: () => fetchNotifications(guestOpts),
      fetchNotification: (id: number) => fetchNotification(id, guestOpts),
      fetchQuestionnaires: () => fetchQuestionnaires(guestOpts),
      fetchQuestionnaire: (id: number) => fetchQuestionnaire(id, guestOpts),
      fetchStamp: (stampId: number) => fetchStamp(stampId, guestOpts),
      fetchMenus: () => fetchMenus(guestOpts),
      postTracking: (body: Parameters<typeof postTracking>[0]) => postTracking(body, guestOpts),
      fetchMynaviAreas: (all?: boolean) => fetchMynaviAreas(all, guestOpts),
      fetchMynaviCollegeTypes: () => fetchMynaviCollegeTypes(guestOpts),
      fetchMynaviCollegesIncremental: (q: string) => fetchMynaviCollegesIncremental(q, guestOpts),
      // TODO(#3215): 削除予定。学校名キーワード検索は /filter の q に統合済みで未使用
      fetchMynaviCollegesSearch: (params: FetchMynaviCollegesSearchParams) =>
        fetchMynaviCollegesSearch(params, guestOpts),
      fetchMynaviCollegesFilter: (params: FetchMynaviCollegesFilterParams) =>
        fetchMynaviCollegesFilter(params, guestOpts),
      fetchMynaviCollegesFilterCount: (params: Omit<FetchMynaviCollegesFilterParams, 'page' | 'limit'>) =>
        fetchMynaviCollegesFilterCount(params, guestOpts),
      fetchMynaviCollegeDetail: (id: number, excludePast?: boolean) =>
        fetchMynaviCollegeDetail(id, excludePast, guestOpts),
      fetchMynaviStudyFields: (all?: boolean) => fetchMynaviStudyFields(all, guestOpts),
      fetchMynaviJobCategories: (all?: boolean) => fetchMynaviJobCategories(all, guestOpts),
      fetchMynaviOpenCampusCount: (date: string) => fetchMynaviOpenCampusCount(date, guestOpts),
      // EC（体験チケット / #3258）: 商品一覧は未認証でも閲覧可（購入可否 purchasable はサーバがユーザー文脈で精緻化）
      fetchProducts: (params?: ListProductsParams) => fetchProducts(params, guestOpts),
      // QR特典（#3262）: 詳細/状態は認証任意（公開）
      fetchQrReward: (id: number) => fetchQrReward(id, guestOpts),
      // QR特典 獲得（#3262）: 認証任意。未ログイン時は guest ヘッダーで serial により獲得（Bearer none 回避）
      acquireQrReward: (id: number, serial?: string | null) => acquireQrReward(id, serial, guestOpts),
      // 拡散用SNS 情報取得（#3285）: 認証任意（ARフォト等で context を指定して取得）
      fetchSpotShareSns: (spotId: number, params: Parameters<typeof fetchSpotShareSns>[1]) =>
        fetchSpotShareSns(spotId, params, guestOpts),
      // 拡散用SNS タップ記録（#3285）: 認証任意。未ログイン時は guest ヘッダーで記録（Bearer none 回避）
      recordShareSnsTap: (spotId: number, body: Parameters<typeof recordShareSnsTap>[1]) =>
        recordShareSnsTap(spotId, body, guestOpts),
    },
    auth: {
      fetchUserProfile: () => fetchUserProfile(authOpts),
      postUserRecovery: (recoveryCode: string) => postUserRecovery(recoveryCode, authOpts),
      // シート一覧（公開＋トークンで購入連動ロックを反映。#3281 COURSE_UNLOCK のスタンプ表示に必要）
      fetchSheets: () => fetchSheets(authOpts),
      fetchUserSheets: () => fetchUserSheets(authOpts),
      fetchUserRewards: () => fetchUserRewards(authOpts),
      fetchUserReward: (id: number) => fetchUserReward(id, authOpts),
      fetchUserStamp: (stampId: number) => fetchUserStamp(stampId, authOpts),
      fetchUserStamps: () => fetchUserStamps(authOpts),
      fetchNotification: (id: number) => fetchNotification(id, authOpts),
      fetchNotificationsUnread: () => fetchNotificationsUnread(authOpts),
      fetchStampQuizzes: (stampId: number) => fetchStampQuizzes(stampId, authOpts),
      fetchQuestionnairesType: (type: 'register' | 'reward') => fetchQuestionnairesType(type, authOpts),
      postReward: (id: number, body?: Record<string, unknown>) => postReward(id, body, authOpts),
      postStamp: (stampId: number, body?: Record<string, unknown>) => postStamp(stampId, body, authOpts),
      postStampStrict: (stampId: number, body?: Record<string, unknown>) => postStampStrict(stampId, body, authOpts),
      postStampQuizAnswer: (stampId: number, quizId: number, body?: Record<string, unknown>) =>
        postStampQuizAnswer(stampId, quizId, body, authOpts),
      postQuestionnaireAnswer: (id: number, body?: Record<string, unknown>) =>
        postQuestionnaireAnswer(id, body, authOpts),
      postUserRewardTransaction: (transactionId: number) => postUserRewardTransaction(transactionId, authOpts),
      postRewardApplicationForm: (transactionId: number, body: ApplicationFormSubmitRequest) =>
        postRewardApplicationForm(transactionId, body, authOpts),
      postOcrStamp: (stampId: number, body?: Record<string, unknown>) => postOcrStamp(stampId, body, authOpts),
      fetchOcrScanRequest: (ocrId: number) => fetchOcrScanRequest(ocrId, authOpts),
      postOcrScanRequest: (body: Parameters<typeof postOcrScanRequest>[0]) => postOcrScanRequest(body, authOpts),
      postTracking: (body: Parameters<typeof postTracking>[0]) => postTracking(body, authOpts),
      postSpotCouponRedeem: (spotId: number, couponId: number) => postSpotCouponRedeem(spotId, couponId, authOpts),
      postCouponRedeem: (params: CouponRedeemParams) => postCouponRedeem(params, authOpts),
      fetchSpotCoupons: (id: number) => fetchSpotCoupons(id, authOpts),
      // スポット一覧/詳細（公開＋トークンで購入連動ロックを反映。未購入シートに紐づくスポットを除外）
      fetchSpots: () => fetchSpots(authOpts),
      fetchSpot: (id: number) => fetchSpot(id, authOpts),
      fetchSpotWithLocation: (id: number, latitude: number, longitude: number) =>
        fetchSpotWithLocation(id, latitude, longitude, authOpts),
      postSpotPhotoFrames: (id: number, body?: Record<string, unknown>) => postSpotPhotoFrames(id, body, authOpts),
      postUserReset: () => postUserReset(authOpts),
      fetchUserActivities: (cursor?: string | null) => fetchUserActivities(cursor, authOpts),
      // 認証オプションエンドポイント（isWished フラグ取得 / wishedOnly=true 時は auth 必須）
      // TODO(#3215): 削除予定。学校名キーワード検索は /filter の q に統合済みで未使用
      fetchMynaviCollegesSearch: (params: FetchMynaviCollegesSearchParams) =>
        fetchMynaviCollegesSearch(params, authOpts),
      fetchMynaviCollegesFilter: (params: FetchMynaviCollegesFilterParams) =>
        fetchMynaviCollegesFilter(params, authOpts),
      fetchMynaviCollegesFilterCount: (params: Omit<FetchMynaviCollegesFilterParams, 'page' | 'limit'>) =>
        fetchMynaviCollegesFilterCount(params, authOpts),
      fetchMynaviCollegeDetail: (id: number, excludePast?: boolean) =>
        fetchMynaviCollegeDetail(id, excludePast, authOpts),
      // 認証必須エンドポイント
      fetchMynaviOpenCampuses: (lat: number, lon: number) => fetchMynaviOpenCampuses(lat, lon, authOpts),
      fetchMynaviEmailCheck: () => fetchMynaviEmailCheck(authOpts),
      postMynaviEmail: (email: string) => postMynaviEmail(email, authOpts),
      postMynaviEmailVerify: (email: string, code: string) => postMynaviEmailVerify(email, code, authOpts),
      postMynaviEmailResend: (email: string) => postMynaviEmailResend(email, authOpts),
      postMynaviWishToVisit: (openCampusId: number, isWished: boolean) =>
        postMynaviWishToVisit(openCampusId, isWished, authOpts),
      // 認証必須エンドポイント（ポイント取引履歴）
      fetchMynaviPointTransactions: (params: FetchMynaviPointTransactionsParams) =>
        fetchMynaviPointTransactions(params, authOpts),
      fetchUserRewardTransactions: (cursor?: string | null) => fetchUserRewardTransactions(cursor, authOpts),
      postRewardTransactionPhotoFrames: (transactionId: number) =>
        postRewardTransactionPhotoFrames(transactionId, authOpts),
      // EC（体験チケット / #3258）
      // 商品一覧: 認証済みは Authorization 付きで取得（未認証は「前提購入なし商品のみ」に絞られるため）
      fetchProducts: (params?: ListProductsParams) => fetchProducts(params, authOpts),
      createCheckoutSession: (body: CreateCheckoutSessionRequest) => createCheckoutSession(body, authOpts),
      fetchCheckoutSessionDetail: (sessionId: string) => fetchCheckoutSessionDetail(sessionId, authOpts),
      fetchTicketOrders: () => fetchTicketOrders(authOpts),
      // 入退室メッセージ（Geofence / #3259）
      fetchGeofenceAreas: () => fetchGeofenceAreas(authOpts),
      recordGeofenceEvent: (id: number, body: Parameters<typeof recordGeofenceEvent>[1]) =>
        recordGeofenceEvent(id, body, authOpts),
      // QR特典 獲得（#3262）: 認証任意（ログイン時は Authorization を送出、認証OFFは serial を使用）
      acquireQrReward: (id: number, serial?: string | null) => acquireQrReward(id, serial, authOpts),
      // 拡散用SNS タップ記録（#3285）: 認証任意（ログイン時は Authorization を送出）
      recordShareSnsTap: (spotId: number, body: Parameters<typeof recordShareSnsTap>[1]) =>
        recordShareSnsTap(spotId, body, authOpts),
    },
    pure: {
      mergeRewardsWithUserData,
      mergeRewardDetail,
    },
  }
}

export type BoundApi = ReturnType<typeof createApi>
